import { Component, HostListener, Input, OnInit } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { MessageService } from '@core/services';
import { FotoService } from '@features/apr/services';
import { MessagesUtil } from '@core/utils';
import { delay } from 'rxjs/operators';

interface FotoItem {
  url: SafeUrl | null;
  nomeArquivo: string;
  carregando: boolean;
}

@Component({
  selector: 'app-foto-galeria',
  templateUrl: './foto-galeria.component.html',
  styleUrls: ['./foto-galeria.component.css']
})
export class FotoGaleriaComponent implements OnInit {

  @Input() codigoFormulario!: string;
  @Input() titulo = 'Evidências';

  fotos: FotoItem[] = [];
  isCarregando = true;
  isBaixandoTodas = false;
  indiceCarrossel = 0;
  lightboxAberto = false;
  indiceLightbox = 0;
  zoomAtivo = false;
  zoomOrigin = '50% 50%';

  constructor(
    private readonly fotoService: FotoService,
    private readonly sanitizer: DomSanitizer,
    private readonly messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.carregarFotos();
  }

  get fotosVisiveis(): FotoItem[] {
    return this.fotos.slice(this.indiceCarrossel, this.indiceCarrossel + 4);
  }

  get fotosCarregadas(): FotoItem[] {
    return this.fotos.filter(f => !f.carregando);
  }

  abrirLightbox(indice: number): void {
    if (this.fotos[indice].carregando) return;
    this.indiceLightbox = indice;
    this.lightboxAberto = true;
  }

  fecharLightbox(): void {
    this.lightboxAberto = false;
    this.zoomAtivo = false;
    this.zoomOrigin = '50% 50%';
  }

  toggleZoom(event: MouseEvent): void {
    if (this.zoomAtivo) {
      this.zoomAtivo = false;
      this.zoomOrigin = '50% 50%';
      return;
    }

    const img = event.currentTarget as HTMLElement;
    const rect = img.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;

    this.zoomOrigin = `${x}% ${y}%`;
    this.zoomAtivo = true;
  }

  proximaFotoLightbox(): void {
    this.indiceLightbox = (this.indiceLightbox + 1) % this.fotosCarregadas.length;
    this.zoomAtivo = false;
    this.zoomOrigin = '50% 50%';
  }

  fotoAnteriorLightbox(): void {
    this.indiceLightbox = (this.indiceLightbox - 1 + this.fotosCarregadas.length) % this.fotosCarregadas.length;
    this.zoomAtivo = false;
    this.zoomOrigin = '50% 50%';
  }

  proximaFotoCarrossel(): void {
    this.indiceCarrossel = Math.min(this.indiceCarrossel + 1, this.fotos.length - 1);
  }

  fotoAnteriorCarrossel(): void {
    this.indiceCarrossel = Math.max(this.indiceCarrossel - 1, 0);
  }

  baixarTodas(): void {
    this.isBaixandoTodas = true;
    this.messageService.showLoading(MessagesUtil.get('galeria.fotos.baixando'));
    this.fotoService.downloadTodasFotos(this.codigoFormulario);
    this.messageService.clearLoading();
    this.isBaixandoTodas = false;
  }

  baixarFoto(foto: FotoItem): void {
    if (!foto.url) return;
    const a = document.createElement('a');
    a.href = (foto.url as any).changingThisBreaksApplicationSecurity;
    a.download = foto.nomeArquivo;
    a.click();
  }

  @HostListener('document:keydown.escape')
  onEsc(): void {
    if (this.lightboxAberto) this.fecharLightbox();
  }

  @HostListener('document:keydown.arrowleft')
  onArrowLeft(): void {
    if (this.lightboxAberto && !this.zoomAtivo) this.fotoAnteriorLightbox();
  }

  @HostListener('document:keydown.arrowright')
  onArrowRight(): void {
    if (this.lightboxAberto && !this.zoomAtivo) this.proximaFotoLightbox();
  }

  private carregarFotos(): void {
    this.fotoService.getUrisFromPt(this.codigoFormulario).subscribe({
      next: (uris: string[]) => {
        this.fotos = uris.map(uri => ({
          url: null,
          nomeArquivo: uri.split('/').pop() ?? uri,
          carregando: true
        }));

        this.isCarregando = false;

        uris.forEach((uri, index) => {
          this.fotoService.getFotoBlob(uri).subscribe({
            next: (response: any) => {
              this.fotos[index] = {
                url: this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(response.body)),
                nomeArquivo: this.fotos[index].nomeArquivo,
                carregando: false
              };
            }
          });
        });
      },
      error: () => {
        this.isCarregando = false;
      }
    });
  }
}
